<template>
    <div >
        <!-- =============== Page Title ================-->
        <b-loading :is-full-page="true" :active="isLoading"></b-loading>
        <div class="level is-vcentered">
            <div class="level-item has-text-centered">
                <h1 class="title is-size-4">Password Change</h1>
            </div>
        </div>
        <form @submit.prevent="changePassword">
            <div class='password-change-container box'>
                <b-notification type="is-danger" v-if="errorMessage" :closable="false">
                    {{ errorMessage }}
                </b-notification>
                <div class="columns">
                    <div class="column">
                        <section>
                            <b-field label="New Password" horizontal>
                                <input class="input has-icons-right" type="password" v-model="password" placeholder="New Password" required/>
                            </b-field>
                            <b-field label="Confirm Password" horizontal>
                                <input class="input has-icons-right" type="password" v-model="confirmPassword" placeholder="Confirm New Password" required/>
                            </b-field>
                            <b-field horizontal><!-- Label left empty for spacing -->
                                <button type="submit" class="button is-primary" :disabled="!isPasswordRequirementMet() || isSaving">
                                    Confirm
                                    <i v-if="isSaving" class="fas fa-spinner fa-spin is-small"></i>
                                </button>
                            </b-field>
                            <b-notification type="is-success" v-if="isPasswordSuccessfullyChanged" :closable="false">
                                Password successfully changed. Please click
                                <a href="/">here</a>
                                to login.
                            </b-notification>
                        </section>
                    </div>
                    <div class="column password-requirements">
                        <p><i class="fas is-small pw-req-icon" :class="getPasswordRequirementIconClass(haveAtLeastEightCharacter)"></i>Minimum 8 characters.</p>
                        <p><i class="fas is-small pw-req-icon" :class="getPasswordRequirementIconClass(haveAtLeastOneLowerCaseCharacter && haveAtLeastOneUpperCaseCharacter)"></i>Contains both upper and lower case characters.</p>
                        <p><i class="fas is-small pw-req-icon" :class="getPasswordRequirementIconClass(haveAtLeastOneNumberAndLetter)"></i>Contains at least one letter and one number.</p>
                        <p><i class="fas is-small pw-req-icon" :class="getPasswordRequirementIconClass(haveAtLeastOneNonAlphaNumericCharacter)"></i>Contains at least one non-alphanumeric character.</p>
                        <p><i class="fas is-small pw-req-icon" :class="getPasswordRequirementIconClass(doesNotContainsDisallowedPasswordFragments)"></i>Should not contain any disallowed words.</p>
                        <p><i class="fas is-small pw-req-icon" :class="getPasswordRequirementIconClass(isConfirmPasswordMatched)"></i>Confirm password matched?</p>
                    </div>
                </div>
            </div>
        </form>
    </div>
</template>

<script>
import PasswordChangeService from '@/services/password-change-service.js';
import _ from 'lodash';
export default {
    name: 'PasswordChange',
    data () {
        return {
            password: '',
            confirmPassword: '',
            key: this.$route.query.key,
            errorMessage: '',
            isSaving: false,
            isPasswordSuccessfullyChanged: false,
            disallowedPasswordFragments: [],
            isLoading: true
        };
    },
    async created () {
        try {
            let result = await PasswordChangeService.getDisallowedPasswordFragments(this.key);
            if (result) {
                this.disallowedPasswordFragments = result.data;
            }
        } catch (error) {
            if (error.response && error.response.data && error.response.data.message) {
                this.errorMessage = error.response.data.message;
            } else {
                this.errorMessage = error.message;
            }
        } finally {
            this.isLoading = false;
        }
    },
    methods: {
        async changePassword () {
            let passwordChangeBody = {
                password: this.password,
                confirmPassword: this.confirmPassword,
                key: this.key
            };

            // Validate the password
            if (!this.isPasswordRequirementMet()) {
                this.errorMessage = 'Password requirements not met.';
                return;
            }

            if (!passwordChangeBody.key) {
                this.errorMessage = 'Invalid request';
                return;
            }

            // Clear old error messages
            this.errorMessage = '';
            this.isSaving = true;

            try {
                await PasswordChangeService.changePassword(passwordChangeBody);
                this.isPasswordSuccessfullyChanged = true;
                this.errorMessage = '';
            } catch (error) {
                if (error.response && error.response.data && error.response.data.message) {
                    // Meant to contain the validation results
                    this.errorMessage = error.response.data.message;
                } else {
                    this.errorMessage = error.message;
                }
            } finally {
                this.isSaving = false;
            }
        },
        isPasswordRequirementMet () {
            return this.haveAtLeastOneLowerCaseCharacter &&
                this.haveAtLeastOneUpperCaseCharacter &&
                this.haveAtLeastOneNonAlphaNumericCharacter &&
                this.haveAtLeastOneNumberAndLetter &&
                this.haveAtLeastEightCharacter &&
                this.doesNotContainsDisallowedPasswordFragments &&
                this.isConfirmPasswordMatched;
        },
        getPasswordRequirementIconClass (isConditionMet) {
            if (this.password.length > 0) {
                return isConditionMet ? 'fa-check-circle is-success-text' : 'fa-times-circle is-danger-text';
            }
            return 'fa-minus-circle is-grey-text';
        }
    },
    computed: {
        haveAtLeastOneLowerCaseCharacter () {
            return this.password.toUpperCase() !== this.password;
        },
        haveAtLeastOneUpperCaseCharacter () {
            return this.password.toLowerCase() !== this.password;
        },
        haveAtLeastOneNonAlphaNumericCharacter () {
            return this.password.match('([^a-zA-Z0-9])') !== null;
        },
        haveAtLeastOneNumberAndLetter () {
            return this.password.match('(?=.*[a-zA-Z])(?=.*[0-9]).+') !== null;
        },
        haveAtLeastEightCharacter () {
            return this.password.length >= 8;
        },
        doesNotContainsDisallowedPasswordFragments () {
            let containsDisallowedFragments = _.find(this.disallowedPasswordFragments, (f) => this.password.toLowerCase().includes(f.toLowerCase()));
            return !containsDisallowedFragments;
        },
        isConfirmPasswordMatched () {
            return this.password === this.confirmPassword;
        }
    }
};
</script>

<style lang="scss" scoped>
.password-change-container{
    width: 80%;
    margin-left: 10%;
    margin-right: 10%;
}
.password-requirements {
    font-size: 0.9rem;
    .pw-req-icon {
        margin-right: 5px;
    }
    .is-danger-text {
        color: $danger;
    }
    .is-success-text {
        color: $success;
    }
    .is-grey-text {
        color:grey;
    }
}
</style>
